/*
### Třída `TextProcess`
Třída pro přidávání textu do DOM elementu, podporující aktualizaci React komponent:
- **Metoda** `addText(inputElement, text)`:
  - Přidá text do hodnoty (`value`) zadaného vstupního elementu.
  - Pokud je detekována React komponenta, spustí se příslušné `onChange` nebo `onInput` vlastnosti pro zajištění synchronizace.


### Třída `FocusWatcher`
Třída pro sledování naposledy zaměřeného (focused) DOM elementu:
- Konstruktor automaticky nastaví posluchače událostí `focusin` a `focusout`.
- **Vlastnost** `lastItem`: Uchovává odkaz na naposledy zaměřený textový vstup (`INPUT` nebo `TEXTAREA`).


### Třída `MegaWordClient`
Klient pro komunikaci se systémem MegaWord/NovaVoice přes WebSocket:
- **Konstruktor** přijímá uživatelské údaje (`username`, `password`, `showname`).
- **Metoda** `connect(port, timeout)`:
  - Připojí se k WebSocket serveru na zadaném portu.
  - Automatické pokusy o opětovné připojení při výpadku, pokud je `automaticReconnect` nastavena na `true`.
- **Metoda** `send(data)`:
  - Odesílá data na server a očekává odpověď.
- **Metoda** `close()`:
  - Zavře připojení k WebSocket serveru.
- Události:
  - `receivedText`: Text přijatý ze serveru.
  - `lockFocus`: Indikace uzamčení fokusu na aktuální položku.
  - `unlockFocus`: Indikace uvolnění fokusu.
  - `AddToTitle`: Automaticky identifikuje okno prohlížeče a mění jeho titulek na základě pokynu ze serveru.
    Pokud server pošle text, titul okna se dočasně aktualizuje, aby zahrnoval tento text, a poté se vrátí zpět na původní hodnotu.
    Slouží k identifikaci správného okna. 

### Funkce pro inicializaci připojení a správu událostí
- Anonymní funkce (IIFE) inicializuje:
  - Instanci `FocusWatcher` pro sledování zaměřených elementů.
  - Instanci `MegaWordClient` s automatickým připojením a nastavením posluchačů událostí (`lockFocus`, `unlockFocus`, `receivedText`).
    - V kódu je předvyplněn login a výchozí čas pro opakování pokusu o připojení je nastaven na 5 sekund 
- Při obdržení textu z události `receivedText` se text buď zapíše do naposledy zaměřeného vstupního pole, nebo se zobrazí upozornění, pokud vstupní pole není dostupné.

---

Tento kód je navržen pro asynchronní práci s WebSocket serverem a správu dynamických interakcí s DOM elementy v prostředí React.
*/

// Class for adding text to DOM element
class TextProcess {
  static addText(inputElement: HTMLInputElement | HTMLTextAreaElement, text: string): void {
    // Set the value property
    inputElement.value += text;

    // Hack for React - Get the React fiber instance
    const reactInstanceKey = Object.keys(inputElement).find(
      (key) => key.startsWith('__reactInternalInstance$') || key.startsWith('__reactFiber$'),
    );

    if (reactInstanceKey) {
      // Try to find the onChange or onInput props
      const reactInstance = (inputElement as any)[reactInstanceKey];
      const props = reactInstance?.memoizedProps;

      if (props && (props.onChange || props.onInput)) {
        // Create a synthetic event
        const event = {
          target: inputElement,
          currentTarget: inputElement,
          preventDefault: () => {},
          stopPropagation: () => {},
          persist: () => {},
          bubbles: true,
          cancelable: true,
          timeStamp: Date.now(),
          type: 'change',
          isTrusted: true,
        } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

        // Call the React handler directly
        if (props.onChange) props.onChange(event);
        if (props.onInput) props.onInput(event);
      }
    }
  }
}

// Class that helps track last focused DOM element
class FocusWatcher {
  public lastItem: HTMLInputElement | HTMLTextAreaElement | null = null;

  constructor() {
    this.connect();
  }

  private connect(): void {
    window.addEventListener('focusin', this.onFocusIn.bind(this));
    window.addEventListener('focusout', this.onFocusOut.bind(this));
  }

  private onFocusIn(e: FocusEvent): void {
    const target = e.target as HTMLElement;
    if (target.nodeName === 'TEXTAREA' || target.nodeName === 'INPUT') {
      this.lastItem = target as HTMLInputElement | HTMLTextAreaElement;
    }
  }

  private onFocusOut(e: FocusEvent): void {
    // Optional: Handle focus out logic if needed
  }
}

// Class for communication with MegaWord/NovaVoice
class MegaWordClient extends EventTarget {
  private username: string;
  private password: string;
  private showname: string;
  private socket: WebSocket | null = null;
  public automaticReconnect: boolean = true;

  private oldTitle: string | undefined = undefined;
  private oldTitleTimeout: number | undefined = undefined;

  constructor(username: string, password: string, showname: string) {
    super();
    this.username = username;
    this.password = password;
    this.showname = showname;
  }

  connect(port: number, timeout: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.socket = new WebSocket(`ws://127.0.0.1:${port}`);
      this.socket.onopen = () => {
        this.initCommunication()
          .then((response) => {
            if (response.Result === 'OK') {
              this.registerMessageHandler();
              resolve(response);
            } else {
              reject(response);
            }
          })
          .catch((error) => {
            console.error('SocketAPI: Connection error:', error);
            this.automaticReconnect = false;
          });
      };

      this.socket.onerror = (error: Event) => {
        reject({ Result: 'ERROR', Message: (error as ErrorEvent).message });
      };

      this.socket.onclose = () => {
        if (this.automaticReconnect) {
          console.log(
            'SocketAPI: Connection closed, attempting to reconnect in ' + timeout + ' ms ...',
          );
          setTimeout(() => this.reconnect(port, timeout), timeout);
        } else {
          console.log('SocketAPI: Connection closed and reconnect is disabled');
        }
      };
    });
  }

  private registerMessageHandler(): void {
    this.socket?.addEventListener('message', (message) => {
      const data = JSON.parse(message.data);
      if (data.Event === 'Text') {
        this.dispatchEvent(new CustomEvent('receivedText', { detail: { text: data.Text } }));
      } else if (data.Event === 'Locked') {
        this.dispatchEvent(new CustomEvent('lockFocus', {}));
      } else if (data.Event === 'Unlocked') {
        this.dispatchEvent(new CustomEvent('unlockFocus', {}));
      } else if (data.Event === 'AddToTitle') {
        console.log('SocketAPI: Set new title to', data.Text);
        if (!this.oldTitle) this.oldTitle = document.title;
        if (this.oldTitleTimeout) {
          clearTimeout(this.oldTitleTimeout);
          this.oldTitleTimeout = undefined;
        }
        if (data.Text === '') {
          document.title = this.oldTitle;
          this.oldTitle = undefined;
          return;
        }
        document.title = this.oldTitle + ' ' + data.Text;
        this.oldTitleTimeout = window.setTimeout(() => {
          document.title = this.oldTitle!;
          this.oldTitle = undefined;
          this.oldTitleTimeout = undefined;
        }, 2000);
      } else {
        console.log('SocketAPI: Received ignored message:', data);
        /*
                  Possible messages:
                    "Started", "User has started recognition"
                    "Stopped", "User has stopped recognition"
                    "Close", "Program is closing"
                */
      }
    });
  }

  private reconnect(port: number, timeout: number): void {
    this.connect(port, timeout)
      .then(() => {
        console.log('SocketAPI: Reconnected');
      })
      .catch((error) => {
        //console.error("SocketAPI: Reconnection error:", error);
      });
  }

  private initCommunication(): Promise<any> {
    return this.send({
      Method: 'InitCommunication',
      Protocol: '1.0.0',
      User: this.username,
      Password: this.password,
      ShowName: this.showname,
    });
  }

  send(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.socket?.send(JSON.stringify(data));

      const handleMessage = (message: MessageEvent) => {
        this.socket?.removeEventListener('message', handleMessage);
        const response = JSON.parse(message.data);
        if (response.Result === 'OK') {
          resolve(response);
        } else {
          reject(response);
        }
      };

      this.socket?.addEventListener('message', handleMessage);
    });
  }

  close(): void {
    if (this.socket) {
      this.socket.onclose = () => {
        console.log('SocketAPI: Connection closed');
      };
      this.socket.close();
      this.socket = null;
    }
  }
}

export const initNovaVoice = async () => {
  const focusWatcher = new FocusWatcher();
  const myConn = new MegaWordClient('OR-CZ', 'eWQ0KDalSDu5ToqGNsyP', 'Spojení se systémem OR-CZ');
  myConn.automaticReconnect = true;

  try {
    await myConn.connect(24803, 5000); // default port is 24803 and timeout is 5 sec
    console.log('SocketAPI: Connection initialized');
  } catch (error) {
    console.error('SocketAPI: Initialization error:', error);
  }

  myConn.addEventListener('lockFocus', (event: Event) => {
    (myConn as any).lockedToItem = focusWatcher.lastItem;
    console.log('SocketAPI: Lock focus to item', (myConn as any).lockedToItem);
  });

  myConn.addEventListener('unlockFocus', (event: Event) => {
    console.log('SocketAPI: Unlock focus');
    (myConn as any).lockedToItem = undefined;
  });

  myConn.addEventListener('receivedText', (event: any) => {
    console.log('SocketAPI: Received text', event.detail.text);
    if ((myConn as any).lockedToItem) {
      TextProcess.addText((myConn as any).lockedToItem, event.detail.text);
    } else {
      alert('Received text, but no idea where to write it: ' + event.detail.text);
    }
  });

  return myConn;
};

/*// Function to initialize connection on page init and handle reconnections
(async function () {
  const focusWatcher = new FocusWatcher();
  const myConn = new MegaWordClient('OR-CZ', 'eWQ0KDalSDu5ToqGNsyP', 'Spojení se systémem OR-CZ');
  myConn.automaticReconnect = true;

  try {
    await myConn.connect(24803, 5000); // default port is 24803 and timeout is 5 sec
    console.log('SocketAPI: Connection initialized');
  } catch (error) {
    console.error('SocketAPI: Initialization error:', error);
  }

  myConn.addEventListener('lockFocus', (event: Event) => {
    (myConn as any).lockedToItem = focusWatcher.lastItem;
    console.log('SocketAPI: Lock focus to item', (myConn as any).lockedToItem);
  });

  myConn.addEventListener('unlockFocus', (event: Event) => {
    console.log('SocketAPI: Unlock focus');
    (myConn as any).lockedToItem = undefined;
  });

  myConn.addEventListener('receivedText', (event: any) => {
    console.log('SocketAPI: Received text', event.detail.text);
    if ((myConn as any).lockedToItem) {
      TextProcess.addText((myConn as any).lockedToItem, event.detail.text);
    } else {
      alert('Received text, but no idea where to write it: ' + event.detail.text);
    }
  });

  // Uncomment the following line to close the connection when needed
  // myConn.close();
})();*/
